import { useApolloClient } from "@apollo/client";
import {
  CurrentUserDomainDocument,
  MediaDataRoomDataPartnerInformationDocument,
  MediaDataRoomPublisherInformationDocument,
} from "@decentriq/graphql/dist/types";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import {
  type MediaDataRoomComputeRequestKey,
  MediaDataRoomJobInput,
} from "features/mediaDataRoom/hooks/v3";
import {
  type MediaDataRoomParticipant,
  MediaDataRoomParticipantRole,
  type MediaDataRoomV3,
} from "features/mediaDataRoom/models";

interface ParticipantsBrandingHookPayload {
  dataRoomId: string;
  participantGroups?: MediaDataRoomV3["state"]["participantGroups"];
  currentUserEmail: string;
  createdBy: string;
  ownerOrgLogo: string | undefined;
  ownerOrgName: string;
}

type ParticipantsBrandingHookResult = UseQueryResult<
  MediaDataRoomParticipant[]
>;

const isMediaDataRoomParticipantRoleValid = (role: string) =>
  Object.values(MediaDataRoomParticipantRole).includes(
    role as MediaDataRoomParticipantRole
  );

const useParticipantsBranding = ({
  dataRoomId,
  createdBy,
  ownerOrgLogo,
  ownerOrgName,
  participantGroups,
  currentUserEmail,
}: ParticipantsBrandingHookPayload): ParticipantsBrandingHookResult => {
  const apolloClient = useApolloClient();
  return useQuery({
    enabled: Boolean(participantGroups?.length),
    queryFn: async () => {
      const participants = participantGroups!.map(
        ({ role, emails, permissions, id }) => ({
          emails,
          id,
          isCurrentUser: emails.includes(currentUserEmail),
          logo: undefined,
          name: `<${emails?.[0]?.split("@")[1] || "Unknown"}>`,
          permissions,
          role: isMediaDataRoomParticipantRoleValid(role)
            ? (role as MediaDataRoomParticipantRole)
            : MediaDataRoomParticipantRole.Custom,
        })
      );
      return await Promise.all(
        participantGroups!.map(async ({ organizationId }, index) => {
          const defaultParticipant = participants[index];
          if (
            organizationId &&
            defaultParticipant.role === MediaDataRoomParticipantRole.Publisher
          ) {
            try {
              const { data } = await apolloClient.query({
                query: MediaDataRoomPublisherInformationDocument,
                variables: {
                  dataRoomId,
                  organizationId,
                },
              });
              return {
                ...defaultParticipant,
                logo: data.publishedMediaInsightsDcr.publisherInformation.logo,
                name:
                  data.publishedMediaInsightsDcr.publisherInformation.name ??
                  defaultParticipant.name,
              };
            } catch {
              return defaultParticipant;
            }
          }
          if (
            organizationId &&
            defaultParticipant.role === MediaDataRoomParticipantRole.DataPartner
          ) {
            try {
              const { data } = await apolloClient.query({
                query: MediaDataRoomDataPartnerInformationDocument,
                variables: {
                  dataRoomId,
                  organizationId,
                },
              });
              return {
                ...defaultParticipant,
                logo: data.publishedMediaInsightsDcr.dataPartnerInformation
                  .logo,
                name:
                  data.publishedMediaInsightsDcr.dataPartnerInformation.name ??
                  defaultParticipant.name,
              };
            } catch {
              return defaultParticipant;
            }
          }
          if (defaultParticipant.isCurrentUser) {
            try {
              const { data } = await apolloClient.query({
                query: CurrentUserDomainDocument,
              });
              return {
                ...defaultParticipant,
                logo: data.myself.organizationLogo,
                name: data.myself.organization?.name ?? defaultParticipant.name,
              };
            } catch {
              return defaultParticipant;
            }
          }
          if (defaultParticipant.emails.includes(createdBy)) {
            return {
              ...defaultParticipant,
              logo: ownerOrgLogo,
              name: ownerOrgName,
            };
          }
          return defaultParticipant;
        })
      ).catch(() => participants);
    },
    queryKey: MediaDataRoomJobInput.create(
      "participantsBranding" as MediaDataRoomComputeRequestKey,
      dataRoomId
    )
      .withFetchResultsSubKey()
      .buildQueryKey(),
  });
};

export default useParticipantsBranding;
